import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from '../config/axiosConfig';

const UISettingsContext = createContext();

export const useUISettings = () => {
  const context = useContext(UISettingsContext);
  if (!context) {
    throw new Error('useUISettings must be used within UISettingsProvider');
  }
  return context;
};

// Map API values to internal values and vice versa
const API_TO_INTERNAL_THEME = {
  'WhatsApp Green': 'whatsapp',
  'Ocean Blue': 'ocean',
  'Sunset Orange': 'sunset',
  'Forest Green': 'forest',
  'Midnight Purple': 'midnight',
  // Handle lowercase/underscore variants from API
  'whatsapp_green': 'whatsapp',
  'ocean_blue': 'ocean',
  'sunset_orange': 'sunset',
  'forest_green': 'forest',
  'midnight_purple': 'midnight',
  'whatsapp': 'whatsapp',
  'ocean': 'ocean',
  'sunset': 'sunset',
  'forest': 'forest',
  'midnight': 'midnight',
};

const INTERNAL_TO_API_THEME = {
  'whatsapp': 'WhatsApp Green',
  'ocean': 'Ocean Blue',
  'sunset': 'Sunset Orange',
  'forest': 'Forest Green',
  'midnight': 'Midnight Purple',
};

const API_TO_INTERNAL_MODE = {
  'Light Mode': 'light',
  'Dark Mode': 'dark',
  'light': 'light',
  'dark': 'dark',
};

const INTERNAL_TO_API_MODE = {
  'light': 'Light Mode',
  'dark': 'Dark Mode',
};

export const UISettingsProvider = ({ children }) => {
  const [colorTheme, setColorTheme] = useState('whatsapp');
  const [displayMode, setDisplayMode] = useState('light');
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  // Fetch UI settings from API
  const fetchUISettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/company/ui-settings');
      
      if (response.data.statuscode === 200 && response.data.entity) {
        const { colour_theme, display_mode } = response.data.entity;
        
        // Convert API values to internal values
        const internalTheme = API_TO_INTERNAL_THEME[colour_theme] || 'whatsapp';
        const internalMode = API_TO_INTERNAL_MODE[display_mode] || 'light';
        
        setColorTheme(internalTheme);
        setDisplayMode(internalMode);
        
        return { colorTheme: internalTheme, displayMode: internalMode };
      }
    } catch (error) {
      console.error('Error fetching UI settings:', error);
      // Use defaults on error
      return { colorTheme: 'whatsapp', displayMode: 'light' };
    } finally {
      setLoading(false);
    }
  }, []);

  // Update UI settings via API
  const updateUISettings = useCallback(async (newColorTheme, newDisplayMode) => {
    try {
      setUpdating(true);
      
      // Convert internal values to API values
      const apiTheme = INTERNAL_TO_API_THEME[newColorTheme] || newColorTheme;
      const apiMode = INTERNAL_TO_API_MODE[newDisplayMode] || newDisplayMode;
      
      const payload = {
        colour_theme: apiTheme,
        display_mode: apiMode
      };

      const response = await axios.post('/company/preferences', payload);
      
      if (response.data.statuscode === 200) {
        setColorTheme(newColorTheme);
        setDisplayMode(newDisplayMode);
        // message.success(response.data.message || 'UI settings updated successfully');
        return true;
      } else {
        // message.error(response.data.message || 'Failed to update UI settings');
        return false;
      }
    } catch (error) {
      console.error('Error updating UI settings:', error);
    //   message.error(error.response?.data?.message || 'Failed to update UI settings');
      return false;
    } finally {
      setUpdating(false);
    }
  }, []);

  // Update only color theme
  const updateColorTheme = useCallback(async (newColorTheme) => {
    return updateUISettings(newColorTheme, displayMode);
  }, [displayMode, updateUISettings]);

  // Update only display mode
  const updateDisplayMode = useCallback(async (newDisplayMode) => {
    return updateUISettings(colorTheme, newDisplayMode);
  }, [colorTheme, updateUISettings]);

  // Fetch settings on mount
  useEffect(() => {
    fetchUISettings();
  }, [fetchUISettings]);

  const value = {
    colorTheme,
    displayMode,
    loading,
    updating,
    fetchUISettings,
    updateUISettings,
    updateColorTheme,
    updateDisplayMode,
    // Expose setters for optimistic updates (immediate UI feedback)
    setColorTheme,
    setDisplayMode,
  };

  return (
    <UISettingsContext.Provider value={value}>
      {children}
    </UISettingsContext.Provider>
  );
};

export default UISettingsContext;
